import styles from './press-releases.module.scss'
import { CONTENT_TYPES } from '@/constants'
import cn from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { Fragment } from 'react'
import PageHeader from '@/components/PageHeader/PageHeader'
import PressReleaseCard from '@/components/PressReleaseCard'
import SearchForm from '@/components/SearchForm'
import WYSIWYG from '@/components/WYSIWYG/WYSIWYG'
import AppLayout from '@/layouts/AppLayout'
import LayoutService from '@/services/LayoutService'
import PressReleaseService from '@/services/PressReleaseService'
import getSectionData from '@/utils/getSectionData'
import renderCustomIcon from '@/utils/renderCustomIcon'

function Page({ page, pressReleases, layoutData }) {
  const PhoneIcon = renderCustomIcon('phone')
  const MailIcon = renderCustomIcon('mail')
  const pressReleasesPerYear = getPressReleasesPerYear(pressReleases)

  return (
    <AppLayout
      page={page}
      data={layoutData}
    >
      <PageHeader data={page.sections} />

      <header className={cn('header-with-search-form')}>
        <h1>{page.title}</h1>

        <SearchForm type={CONTENT_TYPES[6]} />
      </header>

      <main className={cn(styles['main'])}>
        <div className={cn(styles['person'])}>
          <p>{page.pressReleasesAcf.pressReleaseQuestions.title}</p>

          <div className={cn(styles['person__main'])}>
            <Image
              src={page.pressReleasesAcf.pressReleaseQuestions.portaitPhoto.sourceUrl}
              alt={page.pressReleasesAcf.pressReleaseQuestions.portaitPhoto.altText}
              width="90"
              height="90"
            />

            <div>
              <p>{page.pressReleasesAcf.pressReleaseQuestions.name}</p>

              <ul>
                <li>
                  <Link href={'tel:' + page.pressReleasesAcf.pressReleaseQuestions.emailAddress}>
                    <PhoneIcon />
                    {page.pressReleasesAcf.pressReleaseQuestions.telephoneNumber}
                  </Link>
                </li>

                <li>
                  <Link
                    href={'mailto:' + page.pressReleasesAcf.pressReleaseQuestions.emailAddress}
                    target="_blank"
                  >
                    <MailIcon />
                    {page.pressReleasesAcf.pressReleaseQuestions.emailAddress}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {page.pressReleasesAcf.pressReleaseQuestions.text && (
          <WYSIWYG className={cn(styles['person__text'])}>{page.pressReleasesAcf.pressReleaseQuestions.text}</WYSIWYG>
        )}

        <dl>
          {pressReleasesPerYear.map((year) => {
            return (
              <Fragment key={year.year}>
                <dt>{year.year}</dt>
                <dd>
                  <ul>
                    {year.pressReleases.map((release) => {
                      return (
                        <li key={release.date}>
                          <PressReleaseCard data={release} />
                        </li>
                      )
                    })}
                  </ul>
                </dd>
              </Fragment>
            )
          })}
        </dl>
      </main>
    </AppLayout>
  )
}

export async function getStaticProps() {
  try {
    const { data } = await PressReleaseService().index({ id: 'persberichten' })
    const { data: layoutData } = await LayoutService().index()
    const page = data.page
    const pressReleases = data.pressReleases.nodes
    page.sections = getSectionData(page.sections)

    return {
      props: { page, pressReleases, layoutData },
    }
  } catch (error) {
    throw new Error(error)
  }
}

export default Page

function getPressReleasesPerYear(pressReleases) {
  return pressReleases
    .reduce((accumulator, currentValue) => {
      const date = new Date(currentValue.date)
      const existingYearObject = accumulator.find((entry) => entry.year === date.getFullYear())
      const hasExistingYearObject = !!existingYearObject

      if (hasExistingYearObject) {
        const updatedPressReleases = [...existingYearObject.pressReleases, currentValue]
        const updatedAccumulator = accumulator.map((entry) => {
          return entry.year === date.getFullYear() ? { ...entry, pressReleases: updatedPressReleases } : entry
        })

        return [...updatedAccumulator]
      }

      const YEAR_OBJECT = {
        year: date.getFullYear(),
        pressReleases: [currentValue],
      }

      return [...accumulator, YEAR_OBJECT]
    }, [])
    .sort((entry1, entry2) => entry2.year - entry1.year)
}
